import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';

export interface LeaderboardEntry {
  id: string;
  display_name: string;
  level: number;
  total_score: number;
  words_mastered: number;
  streak_days: number;
  rank: number;
  avatar_url?: string;
}

type Timeframe = 'all-time' | 'monthly' | 'weekly';
type SortBy = 'total_score' | 'level' | 'streak_days';

const fetchLeaderboard = async (timeframe: Timeframe, sortBy: SortBy): Promise<LeaderboardEntry[]> => {
  const { data, error } = await supabase.rpc('get_leaderboard', {
    timeframe_option: timeframe,
    sort_column: sortBy,
  });

  if (error) {
    console.error('Error fetching leaderboard:', error);
    throw new Error(error.message);
  }

  return (data || []).map((entry: any, index: number) => ({
    ...entry,
    rank: index + 1,
  }));
};

const fetchUserRank = async (
  userId: string,
  timeframe: Timeframe,
  sortBy: SortBy
): Promise<LeaderboardEntry | null> => {
  if (!userId) return null;

  const { data, error } = await supabase.rpc('get_user_rank_details', {
    p_user_id: userId,
    timeframe_option: timeframe,
    sort_column: sortBy,
  });

  if (error) {
    console.error('Error fetching user rank:', error);
    throw new Error(error.message);
  }

  return data && data.length > 0 ? data[0] : null;
};

export const useLeaderboard = () => {
  const { user, profile } = useAuthStore();
  const [timeframe, setTimeframe] = useState<Timeframe>('all-time');
  const [sortBy, setSortBy] = useState<SortBy>('total_score');

  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', timeframe, sortBy],
    queryFn: () => fetchLeaderboard(timeframe, sortBy),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const userRankQuery = useQuery({
    queryKey: ['userRank', user?.id, timeframe, sortBy],
    queryFn: () => fetchUserRank(user!.id, timeframe, sortBy),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    leaderboard: leaderboardQuery.data ?? [],
    userRank: userRankQuery.data,
    isLoading: leaderboardQuery.isLoading || userRankQuery.isLoading,
    error: leaderboardQuery.error || userRankQuery.error,
    timeframe,
    setTimeframe,
    sortBy,
    setSortBy,
    profile,
  };
};